<script>
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';

export default {
  components: {
    NextButton,
    SpinnerLoader,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      session: {},
      isLoading: false,
      action: '',
      poller: null,
    };
  },
  computed: {
    status() {
      return this.session.status || 'UNKNOWN';
    },
    isConnected() {
      return this.status.toUpperCase() === 'CONNECTED';
    },
    isQrStatus() {
      return ['INITIALIZING', 'QR', 'QRCODE'].includes(
        this.status.toUpperCase()
      );
    },
    showQr() {
      return this.session.qrcode && this.isQrStatus;
    },
    diagnostics() {
      return this.session.diagnostics || {};
    },
    hasDiagnostics() {
      return Object.keys(this.diagnostics).length > 0;
    },
    diagnosticErrors() {
      return this.diagnostics.errors || [];
    },
    diagnosticNotices() {
      return this.diagnostics.notices || [];
    },
    diagnosticWarning() {
      return this.diagnostics.warning;
    },
    lastChecked() {
      if (!this.diagnostics.checked_at) return '-';
      return new Date(this.diagnostics.checked_at).toLocaleString();
    },
    liveConnectionStatus() {
      return this.booleanLabel(this.diagnostics.is_connected);
    },
    hostStaleStatus() {
      return this.booleanLabel(this.diagnostics.host_device?.stale);
    },
    qrAvailableStatus() {
      return this.booleanLabel(this.diagnostics.qrcode_available);
    },
  },
  mounted() {
    this.fetchSession();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    async fetchSession() {
      try {
        this.isLoading = true;
        const response = await InboxesAPI.getWhatsappWebSession(this.inbox.id);
        this.session = response.data;
        this.syncPolling();
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.WHATSAPP_WEB.LOAD_ERROR')
        );
      } finally {
        this.isLoading = false;
      }
    },
    async runAction(action, requestName) {
      try {
        this.action = action;
        const response = await InboxesAPI[requestName](this.inbox.id);
        this.session = response.data;
        this.syncPolling();
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.WHATSAPP_WEB.ACTION_ERROR')
        );
      } finally {
        this.action = '';
      }
    },
    startSession() {
      this.runAction('start', 'startWhatsappWebSession');
    },
    logoutSession() {
      this.runAction('logout', 'logoutWhatsappWebSession');
    },
    refreshSession() {
      this.runAction('refresh', 'refreshWhatsappWebSession');
    },
    booleanLabel(value) {
      if (value === true) return this.$t('INBOX_MGMT.WHATSAPP_WEB.YES');
      if (value === false) return this.$t('INBOX_MGMT.WHATSAPP_WEB.NO');
      return '-';
    },
    errorLabel(error) {
      return `${error.endpoint}: ${error.message}`;
    },
    noticeLabel(notice) {
      return `${notice.endpoint}: ${notice.message}`;
    },
    syncPolling() {
      if (this.isConnected) {
        this.stopPolling();
        return;
      }
      if (!this.poller) {
        this.poller = window.setInterval(this.refreshSession, 5000);
      }
    },
    stopPolling() {
      if (!this.poller) return;
      window.clearInterval(this.poller);
      this.poller = null;
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex items-center justify-between gap-4">
      <div>
        <h3 class="text-heading-3 text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_WEB.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-0">
          {{ $t('INBOX_MGMT.WHATSAPP_WEB.DESCRIPTION') }}
        </p>
      </div>
      <span
        class="px-3 py-1 rounded-md text-sm font-medium"
        :class="
          isConnected
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-amber-3 text-n-amber-11'
        "
      >
        {{ status }}
      </span>
    </div>

    <div v-if="isLoading" class="flex h-40 items-center justify-center">
      <SpinnerLoader :size="24" class="text-n-blue-9" />
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-[18rem_1fr] gap-6">
      <div
        class="flex items-center justify-center border border-n-weak rounded-lg bg-n-surface-2 min-h-[18rem]"
      >
        <img
          v-if="showQr"
          :src="session.qrcode"
          class="w-64 h-64 object-contain"
          alt="WhatsApp Web QR"
        />
        <div v-else class="text-sm text-n-slate-11 px-4 text-center">
          {{
            isConnected
              ? $t('INBOX_MGMT.WHATSAPP_WEB.CONNECTED')
              : $t('INBOX_MGMT.WHATSAPP_WEB.NO_QR')
          }}
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <label>
          {{ $t('INBOX_MGMT.WHATSAPP_WEB.SESSION') }}
          <input :value="session.session_name" type="text" readonly />
        </label>
        <label>
          {{ $t('INBOX_MGMT.WHATSAPP_WEB.PHONE_NUMBER') }}
          <input :value="session.phone_number || '-'" type="text" readonly />
        </label>
        <label>
          {{ $t('INBOX_MGMT.WHATSAPP_WEB.WEBHOOK_URL') }}
          <input :value="session.webhook_url" type="text" readonly />
        </label>

        <div
          v-if="hasDiagnostics"
          class="border border-n-weak rounded-lg bg-n-surface-1 p-4"
        >
          <div class="text-sm font-medium text-n-slate-12 mb-3">
            {{ $t('INBOX_MGMT.WHATSAPP_WEB.DIAGNOSTICS') }}
          </div>
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <dt class="text-n-slate-11">
                {{ $t('INBOX_MGMT.WHATSAPP_WEB.LAST_CHECKED') }}
              </dt>
              <dd class="text-n-slate-12 mb-0">
                {{ lastChecked }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ $t('INBOX_MGMT.WHATSAPP_WEB.LIVE_CONNECTION') }}
              </dt>
              <dd class="text-n-slate-12 mb-0">
                {{ liveConnectionStatus }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ $t('INBOX_MGMT.WHATSAPP_WEB.HOST_STALE') }}
              </dt>
              <dd class="text-n-slate-12 mb-0">
                {{ hostStaleStatus }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ $t('INBOX_MGMT.WHATSAPP_WEB.QR_AVAILABLE') }}
              </dt>
              <dd class="text-n-slate-12 mb-0">
                {{ qrAvailableStatus }}
              </dd>
            </div>
          </dl>
          <p v-if="diagnosticWarning" class="text-sm text-n-amber-11 mt-3 mb-0">
            {{ diagnosticWarning }}
          </p>
          <ul
            v-if="diagnosticNotices.length"
            class="text-sm text-n-slate-11 mt-3 mb-0"
          >
            <li v-for="notice in diagnosticNotices" :key="notice.endpoint">
              {{ noticeLabel(notice) }}
            </li>
          </ul>
          <ul
            v-if="diagnosticErrors.length"
            class="text-sm text-n-ruby-11 mt-3 mb-0"
          >
            <li v-for="error in diagnosticErrors" :key="error.endpoint">
              {{ errorLabel(error) }}
            </li>
          </ul>
        </div>

        <div class="flex flex-wrap gap-3 mt-2">
          <NextButton
            blue
            solid
            :is-loading="action === 'start'"
            :label="$t('INBOX_MGMT.WHATSAPP_WEB.START')"
            @click="startSession"
          />
          <NextButton
            slate
            faded
            :is-loading="action === 'refresh'"
            :label="$t('INBOX_MGMT.WHATSAPP_WEB.REFRESH')"
            @click="refreshSession"
          />
          <NextButton
            ruby
            faded
            :is-loading="action === 'logout'"
            :label="$t('INBOX_MGMT.WHATSAPP_WEB.LOGOUT')"
            @click="logoutSession"
          />
        </div>
      </div>
    </div>
  </div>
</template>
